import { isNil, get } from 'lodash';

export const DEFAULT_SELECTED_ELEMENTS = { Text: false, Axis: false, Legend: false };

/**
 * Gets the graphic id.
 *
 * @param {object} graphicObject - The graphic object.
 * @returns {string} The id for the graphic or empty string if not found.
 * @throws {Error} If graphic is null or undefined.
 */
export const getId = graphicObject => {
  if (isNil(graphicObject)) {
    throw new Error('graphic not defined');
  }
  return get(graphicObject, '_id', '');
};

/**
 * Get graphic name.
 *
 * @param {object} graphicObject - The graphic object.
 * @returns {string} The name of the graphic or empty string if not found.
 * @throws {Error} If graphic is null or undefined.
 */
export const getName = graphicObject => {
  if (isNil(graphicObject)) {
    throw new Error('graphic not defined');
  }
  return get(graphicObject, 'name', '');
};

/**
 * Gets the type of the graphic.
 *
 * @param {object} graphicObject - The graphic object.
 * @returns {string} The type or empty string if not found.
 * @throws {Error} If graphic is null or undefined.
 */
export const getType = graphicObject => {
  if (isNil(graphicObject)) {
    throw new Error('graphic not defined');
  }
  return get(graphicObject, 'graphicType', '');
};

/**
 * Gets the id of the layer the graphic belongs.
 *
 * @param {object} graphicObject - The graphic object.
 * @returns {string} The id of the layer or empty string if not found.
 * @throws {Error} If graphic is null or undefined.
 */
export const getLayerId = graphicObject => {
  if (isNil(graphicObject)) {
    throw new Error('graphic not defined');
  }
  return get(graphicObject, 'layerId', '');
};

/**
 * Gets the numeric properties.
 *
 * @param {object} graphicObject - The graphic object.
 * @returns {Array} The properties or empty array if not found.
 * @throws {Error} If graphic is null or undefined.
 */
export const getNumericProperties = graphicObject => {
  if (isNil(graphicObject)) {
    throw new Error('graphic not defined');
  }
  return get(graphicObject, 'numericProperties', []);
};

/**
 * Gets the named property.
 *
 * @param {object} graphicObject - The graphic object.
 * @returns {string} The property or empty string if not found.
 * @throws {Error} If graphic is null or undefined.
 */
export const getNamedProperty = graphicObject => {
  if (isNil(graphicObject)) {
    throw new Error('graphic not defined');
  }
  return get(graphicObject, 'namedProperty', '');
};

/**
 * Gets the global properties.
 *
 * @param {object} graphicObject - The graphic object.
 * @returns {Array} The properties or empty array if not found.
 * @throws {Error} If graphic is null or undefined.
 */
export const getGlobalProperties = graphicObject => {
  if (isNil(graphicObject)) {
    throw new Error('graphic not defined');
  }
  return get(graphicObject, 'globalProperties', []);
};

/**
 * Gets an object with the elements and if they are shown or not.
 *
 * @param {object} graphicObject - The graphic object.
 * @returns {object} The object containing the display status of each element or
 * an default object if not found.
 * @throws {Error} If graphic is null or undefined.
 */
export const getSelectedElements = graphicObject => {
  if (isNil(graphicObject)) {
    throw new Error('graphic not defined');
  }
  return get(graphicObject, 'selectedElements', DEFAULT_SELECTED_ELEMENTS);
};

/**
 * Checks if the legend should appear.
 *
 * @param {object} graphicObject - The graphic object.
 * @returns {boolean} True to show the legend, false otherwise.
 * @throws {Error} If graphic is null or undefined.
 */
export const showLegend = graphicObject => {
  if (isNil(graphicObject)) {
    throw new Error('graphic not defined');
  }
  return get(graphicObject, 'selectedElements.Legend', false);
};

/**
 * Checks if the text should appear.
 *
 * @param {object} graphicObject - The graphic object.
 * @returns {boolean} True to show the text, false otherwise.
 * @throws {Error} If graphic is null or undefined.
 */
export const showText = graphicObject => {
  if (isNil(graphicObject)) {
    throw new Error('graphic not defined');
  }
  return get(graphicObject, 'selectedElements.Text', false);
};

/**
 * Checks if the axis should appear.
 *
 * @param {object} graphicObject - The graphic object.
 * @returns {boolean} True to show the axis, false otherwise.
 * @throws {Error} If graphic is null or undefined.
 */
export const showAxis = graphicObject => {
  if (isNil(graphicObject)) {
    throw new Error('graphic not defined');
  }
  return get(graphicObject, 'selectedElements.Axis', false);
};

/**
 * Check if graphic is shared.
 *
 * @param {object} graphicObject - The graphic object.
 * @returns {boolean} True if the graphic is shared and false otherwise.
 * @throws {Error} If graphic is null or undefined.
 */
export const isShared = graphicObject => {
  if (isNil(graphicObject)) {
    throw new Error('graphic not defined');
  }
  return get(graphicObject, 'shared', false);
};

/**
 * Check if graphic is shared.
 *
 * @param {object} graphicObject - The graphic object.
 * @returns {boolean} True if the graphic is shared and false otherwise.
 * @throws {Error} If graphic is null or undefined.
 */
export const isPrivateShared = graphicObject => {
  if (isNil(graphicObject)) {
    throw new Error('graphic not defined');
  }
  return get(graphicObject, 'sharedPrivate', false);
};

/**
 * Get the public id for the graphic.
 *
 * @param {object} graphicObject - The graphic object.
 * @returns {string} The public id for the graphic or an empty string if not found.
 * @throws {Error} If graphic is null or undefined.
 */
export const getPublicId = graphicObject => {
  if (isNil(graphicObject)) {
    throw new Error('graphic not defined');
  }
  return get(graphicObject, 'publicId', '');
};

/**
 * Checks if the graphic is deleted.
 *
 * @param {object} graphicObject - The graphic object.
 * @returns {boolean} True if the graphic is in trash, false otherwise.
 * @throws {Error} If graphic is null or undefined.
 */
export const isDeleted = graphicObject => {
  if (isNil(graphicObject)) {
    throw new Error('graphic not defined');
  }
  return get(graphicObject, 'deleted', '') !== 'no';
};
